import { useDispatch, useSelector } from "react-redux";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import _ from "lodash";
import {
    addDetectionsReviewFilter,
    removeDetectionsReviewFilter,
    addDetectionsTypeFilter,
    removeDetectionsTypeFilter,
    setSelectedRailInspectionImage,
    setCurrentDetection,
    setCurrentInspectionMarkup,
    customAudit,
    setDetectionConditionFilters,
    setDipAngleFilter,
    setGapSizeFilter,
    setAdjustmentSwitchFilters,
    setDefectSizeFilter,
} from "../../../../redux/actions/index";
import { Button, Checkbox, Modal, Slider, Radio } from "antd";

const detectionReviewFiltersSelector = (state) => state.railInspection.detections.reviewFilters;
const detectionTypesSelector = (state) => state.railInspection.detections.types;
const detectionTypeFiltersSelector = (state) => state.railInspection.detections.typeFilters;
const firstRailInspectionImagesSelector = (state) => _.get(state.railInspection.railInspectionImages, "data", []);
const selectedRailInspectionImageSelector = (state) => state.railInspection.selectedRailInspectionImage;
const detectionConditionFiltersSelector = (state) => state.railInspection.detections.detectionConditionFilters || [];
const superAdminSelector = (state) => state.userDetails.userConfig.super_admin;
const sessionIDSelector = (state) => state.playlist.data.routeID;
const conditionTypesSelector = (state) => state.railInspection.inspectionConditionTypes;
const detectionsDataSelector = (state) => state.railInspection.detections.data;
const dipAngleFilterSelector = (state) => state.railInspection.dipAngleFilter;
const gapSizeFilterSelector = (state) => state.railInspection.gapSizeFilter;
const defectSizeFilterSelector = (state) => state.railInspection.defectSizeFilter;
const adjustmentSwitchFiltersSelector = (state) => state.railInspection.adjustmentSwitchFilters;

const DetectionsFiltersModal = ({ detectionsFiltersOpen, setDetectionsFiltersOpen }) => {
    let dispatch = useDispatch();

    const detectionReviewFilters = useSelector(detectionReviewFiltersSelector);
    const detectionTypes = useSelector(detectionTypesSelector);
    const detectionTypeFilters = useSelector(detectionTypeFiltersSelector);
    const selectedRailInspection = useSelector(selectedRailInspectionImageSelector);
    const firstRailInspectionImages = useSelector(firstRailInspectionImagesSelector);
    const detectionConditionFilters = useSelector(detectionConditionFiltersSelector);
    const superAdmin = useSelector(superAdminSelector);
    const conditionTypes = useSelector(conditionTypesSelector);
    const sessionID = useSelector(sessionIDSelector);
    const detectionsData = useSelector(detectionsDataSelector);
    const dipAngleFilter = useSelector(dipAngleFilterSelector);
    const gapSizeFilter = useSelector(gapSizeFilterSelector);
    const defectSizeFilter = useSelector(defectSizeFilterSelector);
    const adjustmentSwitchFilters = useSelector(adjustmentSwitchFiltersSelector);

    const [totalNumberOfFilters, setTotalNumberOfFilters] = useState(false);
    const [activeDetection, setActiveDetection] = useState(null);
    const [activeFilter, setActiveFilter] = useState(null);
    const [selectedDetectionsRadioValue, setSelectedDetectionsRadioValue] = useState("all-detections");
    const [localDefectSizeFilter, setLocalDefectSizeFilter] = useState(defectSizeFilter);

    useEffect(() => {
        if (superAdmin !== undefined && !superAdmin) {
            dispatch(removeDetectionsTypeFilter("Cracked Rail"));
            dispatch(removeDetectionsTypeFilter("Broken Rail"));
            dispatch(removeDetectionsTypeFilter("Special Joint"));
        }
    }, [superAdmin, dispatch]);

    const filteredDetectionTypes = useMemo(() => {
        if (superAdmin === undefined || superAdmin) {
            return detectionTypes;
        } else {
            return _.filter(detectionTypes, (type) => type !== "Cracked Rail" && type !== "Broken Rail" && type !== "Special Joint");
        }
    }, [detectionTypes, superAdmin]);

    const selectDetectionReview = (detectionReview) => {
        if (_.includes(detectionReviewFilters, detectionReview)) {
            dispatch(removeDetectionsReviewFilter(detectionReview));
        } else {
            dispatch(addDetectionsReviewFilter(detectionReview));
        }
    };

    const selectDetectionType = useCallback(
        (detectionType) => {
            if (_.includes(detectionTypeFilters, detectionType)) {
                dispatch(removeDetectionsTypeFilter(detectionType));
            } else {
                dispatch(addDetectionsTypeFilter([...detectionTypeFilters, detectionType]));
                dispatch(
                    customAudit(
                        "inspection_detections_navigator.filter",
                        {
                            detectionType: detectionType,
                            session_id: sessionID,
                        },
                        `Inspection detections navigator filter changed: ${detectionType}`,
                    ),
                );
            }
        },
        [detectionTypeFilters, dispatch, sessionID],
    );

    const toggleAllDetectionsType = useCallback(
        (checked) => {
            // setSelectAllDetectionChecked(checked);

            if (checked) {
                const filtersToAdd = [];
                _.forEach(filteredDetectionTypes, (type) => {
                    if (!_.find(detectionTypeFilters, type)) {
                        filtersToAdd.push(type);
                    }
                });
                dispatch(addDetectionsTypeFilter(filtersToAdd));
            } else {
                _.forEach(detectionTypeFilters, (filter) => {
                    dispatch(removeDetectionsTypeFilter(filter));
                });
            }
        },
        [detectionTypeFilters, dispatch, filteredDetectionTypes],
    );

    useEffect(() => {
        if (detectionTypeFilters.length === 0) {
            setSelectedDetectionsRadioValue("no-detections");
        } else if (detectionTypeFilters.length === filteredDetectionTypes.length) {
            setSelectedDetectionsRadioValue("all-detections");
        } else {
            setSelectedDetectionsRadioValue("partial-detections");
        }
    }, [detectionTypeFilters.length, filteredDetectionTypes.length]);

    const detectionFilters = useMemo(() => {
        const groupedDetections = _.groupBy(
            detectionsData.filter((detection) => {
                if (!detectionReviewFilters.length) {
                    return true;
                }
                return _.includes(detectionReviewFilters, detection.review_status);
            }),
            "name",
        );

        if (filteredDetectionTypes && filteredDetectionTypes.length) {
            return (
                <>
                    {filteredDetectionTypes.sort().map((name, idx) => {
                        return (
                            <div
                                className={`DetectionTypeCheckbox ${_.includes(detectionTypeFilters, name) ? "checked" : ""} ${idx === activeDetection ? "active" : ""}`}
                                onClick={() => {
                                    setActiveDetection(idx);
                                    setActiveFilter(name);
                                    if (!_.includes(detectionTypeFilters, name)) {
                                        selectDetectionType(name);
                                    }
                                }}>
                                <Checkbox
                                    key={idx}
                                    checked={_.includes(detectionTypeFilters, name)}
                                    onChange={() => {
                                        selectDetectionType(name);
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                />
                                <div className="CheckboxLabel">{name}</div>
                                <div className="DetectionsCountLabel">{groupedDetections[name] ? groupedDetections[name].length : 0}</div>
                            </div>
                        );
                    })}
                </>
            );
        } else {
            return <span>No Detections</span>;
        }
    }, [detectionTypeFilters, filteredDetectionTypes, selectDetectionType, activeDetection, detectionsData, detectionReviewFilters]);

    const toggleDetectionCondition = useCallback(
        (e, condition, type) => {
            let listToUpdate = _.get(detectionConditionFilters, [type, condition]);

            if (e.target.checked) {
                listToUpdate = _.uniq([...listToUpdate, e.target.value]);
            } else {
                listToUpdate = _.filter(listToUpdate, (item) => item !== e.target.value);
            }

            const updatedConditionFilters = _.cloneDeep(detectionConditionFilters);
            updatedConditionFilters[type][condition] = listToUpdate;

            dispatch(setDetectionConditionFilters(updatedConditionFilters));
        },
        [detectionConditionFilters, dispatch],
    );

    const toggleAllDetectionConditions = useCallback(
        (e, condition, type, dataValues) => {
            const updatedConditionFilters = _.cloneDeep(detectionConditionFilters);

            if (e.target.checked) {
                const listToUpdate = Object.keys(dataValues).map((value) => (typeof dataValues[value] === "string" ? dataValues[value] : value));
                listToUpdate.push("none");
                updatedConditionFilters[type][condition] = listToUpdate;
            } else {
                updatedConditionFilters[type][condition] = [];
            }

            dispatch(setDetectionConditionFilters(updatedConditionFilters));
        },
        [detectionConditionFilters, dispatch],
    );

    const setGapSize = useCallback(
        (e) => {
            dispatch(setGapSizeFilter(e));
        },
        [dispatch],
    );

    const setDipAngle = useCallback(
        (e) => {
            dispatch(setDipAngleFilter(e));
        },
        [dispatch],
    );

    useEffect(() => {
        setLocalDefectSizeFilter(defectSizeFilter);
    }, [defectSizeFilter]);

    const debouncedSetDefectSizeFilter = useMemo(() => {
        return _.debounce((range) => {
            dispatch(setDefectSizeFilter(range));
        }, 300);
    }, [dispatch]);

    useEffect(() => {
        return () => debouncedSetDefectSizeFilter.cancel();
    }, [debouncedSetDefectSizeFilter]);

    const maxGapSize = useMemo(() => {
        let maxValue = 10;
        const maxGapJoint = _.maxBy(
            detectionsData.filter((item) => item.name === "Joint" && _.get(item, ["analysis", "gap_mm"]) !== undefined),
            "analysis.gap_mm",
        );
        const maxGap = _.get(maxGapJoint, ["analysis", "gap_mm"], 0);
        if (maxGap > maxValue) {
            setGapSize([0, maxGap]);
            return maxGap;
        }
        return maxValue;
    }, [detectionsData, setGapSize]);

    const maxDipAngle = useMemo(() => {
        let maxValue = 30;
        const maxDipJoint = _.maxBy(
            detectionsData.filter((item) => item.name === "Joint" && _.get(item, ["analysis", "dip_angle_valid"], false)),
            "analysis.dip_angle_mrad",
        );
        const maxDip = _.get(maxDipJoint, ["analysis", "dip_angle_mrad"], 0);

        if (maxDip > maxValue) {
            setDipAngle([0, maxDip]);
            return maxDip;
        }

        return maxValue;
    }, [detectionsData, setDipAngle]);

    const disableSlider = useMemo(() => {
        const dipAngleFilters = _.get(detectionConditionFilters, ["Joint", "dip_angle"], []);
        if (_.includes(dipAngleFilters, "dip_angle_present")) {
            setDipAngle([0, maxDipAngle]);
            return false;
        }
        return true;
    }, [detectionConditionFilters, setDipAngle, maxDipAngle]);

    const disableGapSlider = useMemo(() => {
        const gapSizeFilters = _.get(detectionConditionFilters, ["Joint", "gap_mm"], []);
        if (_.includes(gapSizeFilters, "gap_data") && !_.includes(gapSizeFilters, "none")) {
            setGapSize([0, maxGapSize]);
            return false;
        }
        return true;
    }, [detectionConditionFilters, setGapSize, maxGapSize]);

    const adjustmentSwitchMeasurements = useMemo(() => {
        const adjustmentSwitchObservations = _.filter(detectionsData, (detection) => detection.name === "Adjustment Switch Base Plate");
        if (!adjustmentSwitchObservations.length) {
            return false;
        }

        return _.reduce(
            adjustmentSwitchObservations,
            (acc, cur) => {
                const measurements = _.get(cur, "measurements", {});
                const tipOffset = [measurements.tip_offset_1 ?? 0, measurements.tip_offset_2 ?? 0].sort((a, b) => a - b);
                const gapOffset = [measurements.cut_out_1 ?? 0, measurements.cut_out_2 ?? 0].sort((a, b) => a - b);
                const heelOffset = [measurements.heel_offset_1 ?? 0, measurements.heel_offset_2 ?? 0].sort((a, b) => a - b);

                const updateRange = (accRange, offset) => {
                    if (!accRange.length) {
                        return offset;
                    }
                    return [Math.min(accRange[0], offset[0]), Math.max(accRange[1], offset[1])];
                };

                acc.tip = updateRange(acc.tip, tipOffset);
                acc.gap = updateRange(acc.gap, gapOffset);
                acc.heel = updateRange(acc.heel, heelOffset);

                return acc;
            },
            {
                tip: [],
                gap: [],
                heel: [],
            },
        );
    }, [detectionsData]);

    useEffect(() => {
        if (adjustmentSwitchMeasurements) {
            _.forEach(Object.keys(adjustmentSwitchMeasurements), (filterType) => {
                dispatch(setAdjustmentSwitchFilters(filterType, adjustmentSwitchMeasurements[filterType]));
            });
        }
    }, [adjustmentSwitchMeasurements, dispatch]);

    const conditionFilters = useMemo(() => {
        const filters = [];
        let count = 0;
        if (_.includes(filteredDetectionTypes, activeFilter) && (_.has(conditionTypes, activeFilter) || adjustmentSwitchMeasurements)) {
            let detectionConditions = _.get(conditionTypes, activeFilter, {});
            const detectionFilters = [];

            if (activeFilter === "Joint") {
                detectionConditions = _.merge(
                    {
                        dip_angle: {
                            display_name: "Dip Angle",
                            data_values: {
                                "Dip angle present": "dip_angle_present",
                                "No dip angle found": "no_dip_angle",
                            },
                        },
                        gap_mm: {
                            display_name: "Joint Gap Measurement",
                            data_values: {
                                "Auto measurement taken": "gap_data",
                            },
                        },
                    },
                    detectionConditions,
                );
            }

            if (activeFilter === "Adjustment Switch Base Plate") {
                detectionConditions = _.merge({
                    tip: {
                        display_name: "Tip Support",
                    },
                    gap: {
                        display_name: "Gap",
                    },
                    heel: {
                        display_name: "Heel Support",
                    },
                });
            }

            _.forEach(Object.keys(detectionConditions), (detectionCondition) => {
                const _detectionCondition = detectionConditions[detectionCondition];
                const dataValues = _.get(_detectionCondition, "data_values", {});
                const filterList = _.get(detectionConditionFilters, [activeFilter, detectionCondition], []);
                const allSelected = Object.keys(dataValues).length + 1 === filterList.length;
                let nullCount = 0;
                detectionFilters.push(
                    <div className="detectionReviewConditionFilter">
                        <div className="ConditionFilterTitle">{_detectionCondition.display_name}</div>
                        <div className="DetectionReviewConditionFilterContainer">
                            {dataValues && !_.isEmpty(dataValues) && (
                                <Checkbox
                                    className="detectionReviewConditionCheckboxAll"
                                    onChange={(e) => toggleAllDetectionConditions(e, detectionCondition, activeFilter, dataValues, filterList)}
                                    checked={allSelected}>
                                    {allSelected ? "Deselect all" : "Select all"}
                                </Checkbox>
                            )}

                            {_.map(Object.keys(dataValues), (value) => {
                                const dataValue = typeof dataValues[value] === "string" ? dataValues[value] : value;
                                const checked = filterList.includes(dataValue);
                                if (checked) {
                                    count++;
                                }
                                let _nullCount = 0;
                                const detectionCount = _.filter(detectionsData, (detection) => {
                                    if (detection.name !== activeFilter) {
                                        return false;
                                    }
                                    let detectionClassification = _.get(detection, ["conditions", detectionCondition, "user"], null);
                                    if (!detectionClassification) {
                                        detectionClassification = _.get(detection, ["conditions", detectionCondition, "auto"], null);
                                    }

                                    if (!detectionClassification && activeFilter === "Joint" && detectionCondition === "dip_angle") {
                                        if (detection.analysis.dip_angle_valid && detection.analysis.dip_angle_mrad > 0) {
                                            detectionClassification = "dip_angle_present";
                                        } else if (detection.analysis.dip_angle_valid && detection.analysis.dip_angle_mrad === 0) {
                                            detectionClassification = "no_dip_angle";
                                        }
                                    }

                                    if (!detectionClassification && activeFilter === "Joint" && detectionCondition === "gap_mm") {
                                        if (_.get(detection, "analysis.gap_mm") !== undefined) {
                                            detectionClassification = "gap_data";
                                        }
                                    }

                                    if (!detectionClassification) {
                                        _nullCount++;
                                    }
                                    if (detectionClassification !== dataValue) {
                                        return false;
                                    }

                                    return true;
                                }).length;

                                nullCount = _nullCount;

                                if (dataValue) {
                                    return (
                                        <Checkbox
                                            className="detectionReviewConditionCheckbox"
                                            value={dataValue}
                                            onChange={(e) => toggleDetectionCondition(e, detectionCondition, activeFilter)}
                                            checked={checked}>
                                            {`${typeof dataValues[value] === "string" ? value : _.get(dataValues[value], "display_name", value)} (${detectionCount})`}
                                        </Checkbox>
                                    );
                                }
                            })}

                            {dataValues && !_.isEmpty(dataValues) && (
                                <Checkbox
                                    className="detectionReviewConditionCheckbox"
                                    value="none"
                                    onChange={(e) => toggleDetectionCondition(e, detectionCondition, activeFilter)}
                                    checked={filterList.includes("none")}>
                                    None ({nullCount})
                                </Checkbox>
                            )}

                            {activeFilter === "Defect" && (
                                <div className="detectionReviewConditionSliderContainer">
                                    <p>Estimated Defect Size (mm)</p>
                                    <div className="SliderContainer">
                                        <Slider
                                            className="detectionReviewConditionSlider"
                                            range
                                            value={localDefectSizeFilter}
                                            step={5}
                                            marks={{
                                                0: { label: "0mm", style: { left: 0, transform: "none" } },
                                                1000: { label: "1000mm", style: { right: 0, transform: "none", left: "auto" } },
                                            }}
                                            min={0}
                                            max={1000}
                                            onChange={(e) => {
                                                setLocalDefectSizeFilter(e);
                                                debouncedSetDefectSizeFilter(e);
                                            }}
                                            tipFormatter={(val) => `${val} mm`}
                                            tooltipPlacement="top"
                                        />
                                    </div>
                                </div>
                            )}

                            {activeFilter === "Joint" && detectionCondition === "dip_angle" && (
                                <div className="detectionReviewConditionSliderContainer">
                                    <p>Dip angle (mrad)</p>
                                    <div className="SliderContainer">
                                        <p>0</p>
                                        <Slider
                                            className="detectionReviewConditionSlider"
                                            disabled={disableSlider}
                                            range
                                            value={dipAngleFilter}
                                            min={0}
                                            max={maxDipAngle}
                                            step={0.5}
                                            onChange={(e) => setDipAngle(e)}
                                            tooltipPlacement="bottom"
                                        />
                                        <p>{maxDipAngle}</p>
                                    </div>
                                </div>
                            )}

                            {activeFilter === "Joint" && detectionCondition === "gap_mm" && (
                                <div className="detectionReviewConditionSliderContainer">
                                    <p>Joint Gap Size (mm)</p>
                                    <div className="SliderContainer">
                                        <p>0</p>
                                        <Slider
                                            className="detectionReviewConditionSlider"
                                            disabled={disableGapSlider}
                                            range
                                            value={gapSizeFilter}
                                            min={0}
                                            max={maxGapSize}
                                            step={1}
                                            onChange={(e) => setGapSize(e)}
                                            tooltipPlacement="bottom"
                                        />
                                        <p>{maxGapSize}</p>
                                    </div>
                                </div>
                            )}

                            {activeFilter === "Adjustment Switch Base Plate" && _.has(adjustmentSwitchMeasurements, detectionCondition) && (
                                <div className="detectionReviewConditionSliderContainer">
                                    <div className="SliderContainer">
                                        <p>{adjustmentSwitchMeasurements[detectionCondition][0]}mm</p>
                                        <Slider
                                            className="detectionReviewConditionSlider"
                                            disabled={
                                                adjustmentSwitchMeasurements[detectionCondition][0] === 0 &&
                                                adjustmentSwitchMeasurements[detectionCondition][1] === 0
                                            }
                                            range
                                            value={adjustmentSwitchFilters[detectionCondition]}
                                            min={adjustmentSwitchMeasurements[detectionCondition][0]}
                                            max={adjustmentSwitchMeasurements[detectionCondition][1]}
                                            step={1}
                                            onChange={(e) => dispatch(setAdjustmentSwitchFilters(detectionCondition, e))}
                                            tooltipPlacement="bottom"
                                        />
                                        <p>{adjustmentSwitchMeasurements[detectionCondition][1]}mm</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>,
                );
            });
            filters.push(<div className="detectionReviewConditionType">{detectionFilters}</div>);
        }

        // if image position not moved on big sroller (no red arrow present) setTotalNumberOfFilters
        const filtersSum = count + detectionTypeFilters.length + detectionReviewFilters.length;
        if (filtersSum !== 2 && !selectedRailInspection.timestamp && firstRailInspectionImages.length > 0 && !totalNumberOfFilters) {
            setTotalNumberOfFilters(filtersSum);
        }

        if (filters.length) {
            return <div className="detectionReviewCondition">{filters}</div>;
        }

        return (
            <div>
                No condition filters for <span>{activeFilter}</span>.
            </div>
        );
    }, [
        filteredDetectionTypes,
        activeFilter,
        conditionTypes,
        adjustmentSwitchMeasurements,
        detectionTypeFilters.length,
        detectionReviewFilters.length,
        selectedRailInspection.timestamp,
        firstRailInspectionImages.length,
        totalNumberOfFilters,
        detectionConditionFilters,
        localDefectSizeFilter,
        disableSlider,
        dipAngleFilter,
        maxDipAngle,
        disableGapSlider,
        gapSizeFilter,
        maxGapSize,
        adjustmentSwitchFilters,
        toggleAllDetectionConditions,
        detectionsData,
        toggleDetectionCondition,
        dispatch,
        debouncedSetDefectSizeFilter,
        setDipAngle,
        setGapSize,
    ]);

    // if filters changed in modal and inspection image is NOT selected, select first image so the nearby detections are displayed in modal
    useEffect(() => {
        if (totalNumberOfFilters && !selectedRailInspection.timestamp && firstRailInspectionImages.length > 0) {
            dispatch(setSelectedRailInspectionImage(0, firstRailInspectionImages[0], false));
            dispatch(setCurrentDetection(null));
            dispatch(setCurrentInspectionMarkup(null, null));
        }
    }, [selectedRailInspection, firstRailInspectionImages, totalNumberOfFilters]);

    return (
        <Modal
            className="RailInsectionFilterModal"
            visible={detectionsFiltersOpen}
            width={700}
            onOk={() => {
                setDetectionsFiltersOpen(false);
            }}
            onCancel={() => {
                setDetectionsFiltersOpen(false);
            }}
            footer={[
                <Button
                    type="primary"
                    onClick={() => {
                        setDetectionsFiltersOpen(false);
                    }}>
                    Close
                </Button>,
            ]}>
            <div className="DetectionFiltersContentWrapper">
                <div className="DetectionTypes">
                    <div className="DetectionFiltersTitle">Detection Types</div>
                    <div className="DetectionFiltersToolbarWrapper">
                        <div className="SelectionRadios">
                            <Radio.Group
                                defaultValue="all-detections"
                                value={selectedDetectionsRadioValue}
                                buttonStyle="solid">
                                <Radio.Button
                                    value="all-detections"
                                    onClick={() => {
                                        setSelectedDetectionsRadioValue("all-detections");
                                        toggleAllDetectionsType(true);
                                    }}>
                                    Select all
                                </Radio.Button>
                                <Radio.Button
                                    value="no-detections"
                                    onClick={() => {
                                        setSelectedDetectionsRadioValue("no-detections");
                                        toggleAllDetectionsType(false);
                                    }}>
                                    Deselect all
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                        <div className="DetectionTypeList">{detectionFilters}</div>
                        <div className="ReviewStatus">
                            <p>Review Status</p>
                            <div className="detectionReviewFilter">
                                <div className="ReviewStatusContainer">
                                    <Checkbox
                                        className="detectionReviewFilter-Checkbox"
                                        key={1}
                                        checked={_.includes(detectionReviewFilters, 1)}
                                        onChange={() => {
                                            selectDetectionReview(1);
                                        }}>
                                        Verified
                                    </Checkbox>
                                    <Checkbox
                                        className="detectionReviewFilter-Checkbox"
                                        key={2}
                                        checked={_.includes(detectionReviewFilters, 2)}
                                        onChange={() => {
                                            selectDetectionReview(2);
                                        }}>
                                        Verified & Hidden
                                    </Checkbox>
                                </div>
                                <div className="ReviewStatusContainer">
                                    <Checkbox
                                        className="detectionReviewFilter-Checkbox"
                                        key={0}
                                        checked={_.includes(detectionReviewFilters, 0)}
                                        onChange={() => {
                                            selectDetectionReview(0);
                                        }}>
                                        Needs Review
                                    </Checkbox>
                                    <Checkbox
                                        className="detectionReviewFilter-Checkbox"
                                        key={3}
                                        checked={_.includes(detectionReviewFilters, 3)}
                                        onChange={() => {
                                            selectDetectionReview(3);
                                        }}>
                                        Rejected
                                    </Checkbox>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="ConditionFilters">
                    <div className="DetectionFiltersTitle">{activeFilter ? activeFilter + " " : null}Condition Filters</div>
                    <div className="ConditionFilterList">{activeFilter ? conditionFilters : "Select a detection type to view condition filters."}</div>
                </div>
            </div>
        </Modal>
    );
};

export default DetectionsFiltersModal;
